import {STATUS_RESULTADO_ATIVOS, TypeExecuteTaskResult} from "./index";

/** Linha de GET /execute-task/result/{id} */
export interface ExecutionResult {
    id: number,
    status: number,
    mockup: string | null,
    mockupId: number | null,
    testCase: string | null,
    testCaseId: number | null,
    codigo: string | null,
    nome: string | null,
    dataHoraInicio: string | null,
    dataHoraFim: string | null,
    amount_success: number,
    amount_erros: number,
    hasReport: boolean,
    /** Fila do admin: já entregue ao executor (com janela, pela tela) */
    enviado?: boolean,
    janela?: boolean
}

export type Situacao = 'fila' | 'executando' | 'sucesso' | 'erro' | 'cancelado' | 'interrompido';

export const SITUACOES: Record<Situacao, {label: string, color: string}> = {
    fila:         {label: 'Na fila',      color: 'secondary'},
    executando:   {label: 'Executando',   color: 'info'},
    sucesso:      {label: 'Sucesso',      color: 'success'},
    erro:         {label: 'Com erro',     color: 'danger'},
    cancelado:    {label: 'Cancelado',    color: 'dark'},
    interrompido: {label: 'Interrompido', color: 'warning'},
};

export const isAtivo = (result: ExecutionResult) => STATUS_RESULTADO_ATIVOS.includes(Number(result.status));

/** Com janela: está na fila do admin e a tela ainda precisa entregá-lo ao executor desta máquina */
export const aguardaEntregaLocal = (result: ExecutionResult) =>
    Number(result.status) === TypeExecuteTaskResult.STATUS_AGUARDANDO && result.janela === true && !result.enviado;

export const situacaoResultado = (result: ExecutionResult): Situacao => {
    switch (Number(result.status)) {
        case TypeExecuteTaskResult.STATUS_AGUARDANDO:
            return 'fila';
        case TypeExecuteTaskResult.STATUS_EXECUTANDO:
            return 'executando';
        case TypeExecuteTaskResult.STATUS_CANCELADO:
            return 'cancelado';
        case TypeExecuteTaskResult.STATUS_INTERROMPIDO:
            return 'interrompido';
        default:
            // Casos de operações só reportam falhas: sem erro é sucesso, mesmo com zero sucessos contados
            return Number(result.amount_erros) > 0 ? 'erro' : 'sucesso';
    }
}

/** Falhou e vale reexecutar: terminou com erro ou foi interrompido */
export const isFalha = (result: ExecutionResult) => {
    const situacao = situacaoResultado(result);
    return situacao === 'erro' || situacao === 'interrompido';
}

const chaveCaso = (result: ExecutionResult) =>
    result.testCaseId !== null && result.testCaseId !== undefined
        ? result.testCaseId + ':' + result.mockupId
        : 'resultado:' + result.id;

/**
 * Cada reexecução cria um resultado novo: aqui fica só o mais recente de cada caso × mockup
 * (a lista vem ordenada do mais novo para o mais antigo)
 */
export const ultimaExecucaoPorCaso = (results: Array<ExecutionResult>): Array<ExecutionResult> => {
    const vistos = new Set<string>();
    return results.filter(result => {
        const chave = chaveCaso(result);
        if (vistos.has(chave)) {
            return false;
        }
        vistos.add(chave);
        return true;
    });
}

/** Quantas execuções anteriores do mesmo caso × mockup existem além desta */
export const contarHistorico = (results: Array<ExecutionResult>): Record<number, number> => {
    const porChave: Record<string, Array<ExecutionResult>> = {};
    results.forEach(result => {
        const chave = chaveCaso(result);
        (porChave[chave] = porChave[chave] || []).push(result);
    });
    const historico: Record<number, number> = {};
    Object.values(porChave).forEach(lista => {
        historico[lista[0].id] = lista.length - 1;
    });
    return historico;
}

export const resumoSituacoes = (results: Array<ExecutionResult>): Record<Situacao, number> => {
    const resumo: Record<Situacao, number> = {fila: 0, executando: 0, sucesso: 0, erro: 0, cancelado: 0, interrompido: 0};
    results.forEach(result => {
        resumo[situacaoResultado(result)]++;
    });
    return resumo;
}

/** "29/09/2026 10:49:11" -> Date */
export const parseDataHora = (valor: string | null): Date | null => {
    if (!valor) {
        return null;
    }
    const partes = valor.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
    if (!partes) {
        return null;
    }
    const [, dia, mes, ano, hora, minuto, segundo] = partes.map(Number);
    return new Date(ano, mes - 1, dia, hora, minuto, segundo);
}

export const formatarDuracao = (segundos: number): string => {
    if (segundos < 60) {
        return segundos + 's';
    }
    const minutos = Math.floor(segundos / 60);
    if (minutos < 60) {
        return minutos + 'min ' + String(segundos % 60).padStart(2, '0') + 's';
    }
    return Math.floor(minutos / 60) + 'h ' + String(minutos % 60).padStart(2, '0') + 'min';
}

/** Duração do caso; em execução conta até agora */
export const duracaoResultado = (result: ExecutionResult, agora: Date = new Date()): string | null => {
    const inicio = parseDataHora(result.dataHoraInicio);
    if (!inicio) {
        return null;
    }
    const fim = parseDataHora(result.dataHoraFim) || (situacaoResultado(result) === 'executando' ? agora : null);
    if (!fim) {
        return null;
    }
    return formatarDuracao(Math.max(0, Math.round((fim.getTime() - inicio.getTime()) / 1000)));
}

export const textoBusca = (result: ExecutionResult) =>
    [result.codigo, result.nome, result.testCase, result.mockup, '#' + result.id]
        .filter(Boolean)
        .join(' ')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();

export const normalizarBusca = (valor: string) =>
    valor.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
